import { apiClient as api } from "./httpClient";

// CRM F1: funil de captação (kanban de leads) + conversa de WhatsApp.
// Master é o tenant (crm_* / whatsapp_*); leads inbound chegam pelo webhook da Meta.

export interface PipelineStage {
  id: string;
  name: string;
  color: string | null;
  sortOrder: number;
  isWon: boolean;
  isLost: boolean;
  active: boolean;
  /** Prazo em horas úteis que o lead deve permanecer neste estágio; nulo = sem prazo. */
  maxBusinessHours: number | null;
  /** coluna de atendimento humano: a IA não responde quem está aqui */
  requiresHuman: boolean;
  openLeadCount: number;
}

export interface Pipeline {
  id: string;
  name: string;
  isDefault: boolean;
  active: boolean;
  agentId: string | null;
  agentName: string | null;
  /** false = nenhum lead jamais entrou; só aí a exclusão é permitida */
  everUsed: boolean;
  /** campanhas ativas apontadas para este pipeline — elas também trazem leads, antes do canal */
  activeCampaigns: number;
  stages: PipelineStage[];
}

export interface LeadCard {
  id: string;
  name: string;
  whatsappName: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  patientName: string | null;
  priority: string;
  source: string;
  stageId: string | null;
  sortOrder: number;
  stageEnteredAt: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  wonAt: string | null;
  lostAt: string | null;
  createdAt: string;
  /** no recorte de excluídos: quem tirou do quadro e quando */
  deletedAt: string | null;
  deletedByName: string | null;
}

export interface LeadBoardStage {
  id: string;
  name: string;
  color: string | null;
  sortOrder: number;
  isWon: boolean;
  isLost: boolean;
  leads: LeadCard[];
}

export interface LeadBoard {
  pipelineId: string;
  pipelineName: string;
  stages: LeadBoardStage[];
  unstagedLeads: LeadCard[];
}

export interface LeadActivityItem {
  id: string;
  type: string;
  description: string;
  occurredAt: string;
  userId: string | null;
  userName: string | null;
}

export interface LeadMessage {
  id: string;
  direction: "inbound" | "outbound";
  messageType: string;
  body: string | null;
  caption: string | null;
  fileName: string | null;
  mimeType: string | null;
  messageTimestamp: string;
  status: string | null;
  statusDetail: string | null;
  senderName: string | null;
  /** arquivo já guardado por nós e servível pelo proxy autenticado */
  hasMedia: boolean;
  /** por que a mídia não está aqui — a conversa mostra, não omite o anexo */
  mediaError: string | null;
}

export interface LeadDetail {
  id: string;
  name: string;
  whatsappName: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  source: string;
  priority: string;
  pipelineId: string | null;
  stageId: string | null;
  stageEnteredAt: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  patientName: string | null;
  patientBirthDate: string | null;
  diagnosisNote: string | null;
  notes: string | null;
  contactOrigin: string | null;
  contactOriginNote: string | null;
  attendanceRating: number | null;
  /** O paciente é a própria pessoa que escreveu (nem todo atendimento é de criança). */
  patientIsContact: boolean;
  contactRelationId: string | null;
  lostReason: string | null;
  wonAt: string | null;
  lostAt: string | null;
  convertedPatientId: string | null;
  /** jornada anterior do mesmo contato, encerrada — link para o histórico */
  previousLeadId: string | null;
  createdAt: string;
  activities: LeadActivityItem[];
  messages: LeadMessage[];
}

export interface LeadPayload {
  name: string;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  source: string;
  priority: string;
  patientName: string | null;
  patientBirthDate: string | null;
  diagnosisNote: string | null;
  notes: string | null;
  contactOrigin?: string | null;
  contactOriginNote?: string | null;
  attendanceRating?: number | null;
  patientIsContact?: boolean;
  contactRelationId?: string | null;
}

export interface CreateLeadPayload extends LeadPayload {
  entityId: string;
  pipelineId: string | null;
  stageId: string | null;
  assignedToUserId: string | null;
}

export interface CreatedId {
  id: string;
}

export const LEAD_SOURCES: Record<string, string> = {
  whatsapp: "WhatsApp",
  manual: "Manual",
  website: "Site",
  referral: "Indicação",
  ads: "Anúncio",
  other: "Outro",
};

/** Onde a pessoa encontrou a clínica. Espelha LeadContactOrigins do backend. */
export interface Campaign {
  id: string;
  name: string;
  pipelineId: string;
  pipelineName: string;
  adSourceId: string | null;
  matchPhrase: string | null;
  contactOrigin: string | null;
  active: boolean;
  /** leads que entraram por esta campanha */
  leads: number;
}

/** Cadastro de paciente que já usa o telefone do lead. Não bloqueia: quem atende decide. */
export interface LeadPatientMatch {
  patientId: string;
  patientName: string;
  birthDate: string | null;
  /** onde o telefone bateu: paciente, responsável ou 2º responsável */
  matchedOn: string;
  phone: string | null;
  responsibleName: string | null;
}

export interface ConvertLeadResult {
  patientId: string;
  created: boolean;
  /** link do formulário completo, para mandar à família pelo WhatsApp */
  intakeLink: string | null;
}

export interface CampaignPayload {
  name: string;
  pipelineId: string;
  adSourceId: string | null;
  matchPhrase: string | null;
  contactOrigin: string | null;
}

export const LEAD_CONTACT_ORIGINS: Record<string, string> = {
  indicacao_paciente: "Indicação de paciente ou familiar",
  indicacao_profissional: "Indicação de profissional de saúde",
  instagram: "Instagram",
  facebook: "Facebook",
  google: "Busca no Google",
  site: "Site da clínica",
  ja_foi_paciente: "Já é ou foi paciente",
  convenio: "Convênio ou plano",
  fachada: "Passou em frente / fachada",
  escola: "Escola",
  outro: "Outro",
};

/**
 * Rótulo do campo de detalhe, por origem. Ausente = a origem se explica sozinha e o campo
 * não aparece — perguntar "detalhe" de quem achou no Google só ocupa espaço.
 */
export const LEAD_ORIGIN_DETAIL_LABEL: Record<string, string> = {
  indicacao_paciente: "Quem indicou?",
  indicacao_profissional: "Qual profissional indicou?",
  convenio: "Qual convênio?",
  escola: "Qual escola?",
  outro: "Onde encontrou a clínica?",
};

export const LEAD_PRIORITIES: Record<string, string> = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
};

/** Responsável elegível de um funil (padrão usuários × agentes da IA). */
export interface AssignableUser {
  userId: string;
  name: string;
}

export interface PipelineMemberOption extends AssignableUser {
  member: boolean;
}

/** Canal de WhatsApp cujos leads entram no funil (visão do funil). */
export interface PipelineChannelOption {
  channelId: string;
  name: string;
  displayPhone: string | null;
  linked: boolean;
}

/** Card do painel de pipelines: uso e pendências. */
export interface PipelineSummary {
  id: string;
  name: string;
  isDefault: boolean;
  agentName: string | null;
  channels: string[];
  openLeads: number;
  unassigned: number;
  awaitingReply: number;
  windowClosing: number;
  stale: number;
  newLast7Days: number;
  wonLast30Days: number;
  lostLast30Days: number;
}

/** Lead que passou do prazo do estágio (horas úteis). */
export interface OverdueLead {
  leadId: string;
  leadName: string;
  pipelineId: string;
  pipelineName: string;
  stageName: string;
  maxBusinessHours: number;
  elapsedBusinessHours: number;
  assignedToName: string | null;
  stageEnteredAt: string;
}

/** Parentesco entre quem escreveu e o paciente (catálogo do cadastro de pacientes). */
export interface ParentRelation {
  id: string;
  name: string;
}

export const crmService = {
  getParentRelations: async (entityId: string): Promise<ParentRelation[]> => {
    const response = await api.get(`/crm/leads/parent-relations/by-entity/${entityId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  getOverdueLeads: async (entityId: string): Promise<OverdueLead[]> => {
    const response = await api.get(`/crm/leads/overdue/by-entity/${entityId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  getPipelineSummaries: async (entityId: string): Promise<PipelineSummary[]> => {
    const response = await api.get(`/crm/pipelines/summary/by-entity/${entityId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  getAssignableUsers: async (entityId: string, pipelineId?: string | null): Promise<AssignableUser[]> => {
    const response = await api.get(`/crm/leads/assignable/by-entity/${entityId}`, {
      params: pipelineId ? { pipelineId } : undefined,
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  getPipelineMembers: async (pipelineId: string, entityId: string): Promise<PipelineMemberOption[]> => {
    const response = await api.get(`/crm/pipelines/${pipelineId}/members/by-entity/${entityId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  setPipelineMembers: async (pipelineId: string, entityId: string, userIds: string[]): Promise<void> => {
    await api.put(`/crm/pipelines/${pipelineId}/members`, { entityId, userIds });
  },

  getPipelineChannels: async (pipelineId: string, entityId: string): Promise<PipelineChannelOption[]> => {
    const response = await api.get(`/crm/pipelines/${pipelineId}/channels/by-entity/${entityId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  setPipelineChannels: async (pipelineId: string, entityId: string, channelIds: string[]): Promise<void> => {
    await api.put(`/crm/pipelines/${pipelineId}/channels`, { entityId, channelIds });
  },

  /** onlyMine = true aplica a lista de acesso do pipeline (usar na operação, não na config). */
  getPipelines: async (
    entityId: string,
    includeInactive = false,
    onlyMine = false,
  ): Promise<Pipeline[]> => {
    const response = await api.get(`/crm/pipelines/by-entity/${entityId}`, {
      params: { includeInactive, onlyMine },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  createPipeline: async (entityId: string, name: string, isDefault: boolean): Promise<CreatedId> => {
    const response = await api.post("/crm/pipelines", { entityId, name, isDefault });
    return response.data;
  },

  updatePipeline: async (pipelineId: string, entityId: string, name: string): Promise<void> => {
    await api.put(`/crm/pipelines/${pipelineId}`, { entityId, name });
  },

  setPipelineDefault: async (pipelineId: string, entityId: string): Promise<void> => {
    await api.post(`/crm/pipelines/${pipelineId}/default`, { entityId });
  },

  setPipelineActive: async (pipelineId: string, entityId: string, active: boolean): Promise<void> => {
    await api.post(`/crm/pipelines/${pipelineId}/active`, { entityId, active });
  },

  clonePipeline: async (pipelineId: string, entityId: string, name?: string | null): Promise<CreatedId> => {
    const response = await api.post(`/crm/pipelines/${pipelineId}/clone`, { entityId, name });
    return response.data;
  },

  deletePipeline: async (pipelineId: string, entityId: string): Promise<void> => {
    await api.delete(`/crm/pipelines/${pipelineId}/by-entity/${entityId}`);
  },

  setPipelineAgent: async (pipelineId: string, entityId: string, agentId: string | null): Promise<void> => {
    await api.put(`/crm/pipelines/${pipelineId}/agent`, { entityId, agentId });
  },

  createStage: async (
    pipelineId: string,
    entityId: string,
    name: string,
    color: string | null,
    isWon: boolean,
    isLost: boolean,
    maxBusinessHours: number | null,
    requiresHuman: boolean,
  ): Promise<CreatedId> => {
    const response = await api.post(`/crm/pipelines/${pipelineId}/stages`, {
      entityId, name, color, isWon, isLost, maxBusinessHours, requiresHuman,
    });
    return response.data;
  },

  updateStage: async (
    stageId: string,
    entityId: string,
    name: string,
    color: string | null,
    isWon: boolean,
    isLost: boolean,
    maxBusinessHours: number | null,
    requiresHuman: boolean,
  ): Promise<void> => {
    await api.put(`/crm/pipelines/stages/${stageId}`, {
      entityId, name, color, isWon, isLost, maxBusinessHours, requiresHuman,
    });
  },

  reorderStages: async (pipelineId: string, entityId: string, stageIds: string[]): Promise<void> => {
    await api.post(`/crm/pipelines/${pipelineId}/stages/reorder`, { entityId, stageIds });
  },

  setStageActive: async (stageId: string, entityId: string, active: boolean): Promise<void> => {
    await api.post(`/crm/pipelines/stages/${stageId}/active`, { entityId, active });
  },

  getCampaigns: async (entityId: string, includeInactive = false): Promise<Campaign[]> => {
    const response = await api.get(`/crm/campaigns/by-entity/${entityId}`, {
      params: { includeInactive },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  createCampaign: async (entityId: string, payload: CampaignPayload): Promise<CreatedId> => {
    const response = await api.post("/crm/campaigns", { entityId, ...payload });
    return response.data;
  },

  updateCampaign: async (
    campaignId: string,
    entityId: string,
    payload: CampaignPayload,
  ): Promise<void> => {
    await api.put(`/crm/campaigns/${campaignId}`, { entityId, ...payload });
  },

  setCampaignActive: async (campaignId: string, entityId: string, active: boolean): Promise<void> => {
    await api.post(`/crm/campaigns/${campaignId}/active`, { entityId, active });
  },

  deleteCampaign: async (campaignId: string, entityId: string): Promise<void> => {
    await api.delete(`/crm/campaigns/${campaignId}`, { params: { entityId } });
  },

  getBoard: async (
    entityId: string,
    pipelineId?: string | null,
    deleted = false,
  ): Promise<LeadBoard> => {
    const response = await api.get(`/crm/leads/board/${entityId}`, {
      params: { ...(pipelineId ? { pipelineId } : {}), ...(deleted ? { deleted: true } : {}) },
    });
    return response.data;
  },

  getLead: async (leadId: string, entityId: string): Promise<LeadDetail> => {
    const response = await api.get(`/crm/leads/${leadId}/by-entity/${entityId}`);
    return response.data;
  },

  createLead: async (payload: CreateLeadPayload): Promise<CreatedId> => {
    const response = await api.post("/crm/leads", payload);
    return response.data;
  },

  updateLead: async (leadId: string, entityId: string, payload: LeadPayload): Promise<void> => {
    await api.put(`/crm/leads/${leadId}`, { entityId, ...payload });
  },

  // Mídia da conversa. O MinIO é interno: a API streama sob auth/escopo, e o <img>/<audio> não
  // manda header — daí o blob + object URL. Quem chama revoga ao desmontar.
  getMessageMedia: async (messageId: string, entityId: string): Promise<string> => {
    const response = await api.get(`/crm/leads/messages/${messageId}/media/by-entity/${entityId}`, {
      responseType: "blob",
    });
    return URL.createObjectURL(response.data);
  },

  restoreLead: async (leadId: string, entityId: string): Promise<void> => {
    await api.post(`/crm/leads/${leadId}/restore`, { entityId });
  },

  getPatientMatches: async (leadId: string, entityId: string): Promise<LeadPatientMatch[]> => {
    const response = await api.get(`/crm/leads/${leadId}/patient-matches/by-entity/${entityId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  convertLead: async (
    leadId: string,
    entityId: string,
    payload: {
      patientId?: string | null;
      patientName?: string | null;
      birthDate?: string | null;
      phone?: string | null;
      /** DDI do telefone (só dígitos); vazio = Brasil */
      phoneDdi?: string | null;
      email?: string | null;
      responsibleName?: string | null;
    } = {},
  ): Promise<ConvertLeadResult> => {
    const response = await api.post(`/crm/leads/${leadId}/convert`, { entityId, ...payload });
    return response.data;
  },

  transferLead: async (
    leadId: string,
    entityId: string,
    pipelineId: string,
    stageId: string,
    assignedToUserId: string,
  ): Promise<void> => {
    await api.post(`/crm/leads/${leadId}/transfer`, {
      entityId, pipelineId, stageId, assignedToUserId,
    });
  },

  moveLead: async (
    leadId: string,
    entityId: string,
    stageId: string,
    sortOrder: number,
    lostReason?: string | null,
  ): Promise<void> => {
    await api.post(`/crm/leads/${leadId}/move`, { entityId, stageId, sortOrder, lostReason });
  },

  assignLead: async (leadId: string, entityId: string, assignedToUserId: string | null): Promise<void> => {
    await api.post(`/crm/leads/${leadId}/assign`, { entityId, assignedToUserId });
  },

  addNote: async (leadId: string, entityId: string, note: string): Promise<CreatedId> => {
    const response = await api.post(`/crm/leads/${leadId}/notes`, { entityId, note });
    return response.data;
  },

  sendMessage: async (
    leadId: string,
    entityId: string,
    body: string,
    channelId?: string | null,
  ): Promise<CreatedId> => {
    const response = await api.post(`/crm/leads/${leadId}/messages`, { entityId, body, channelId });
    return response.data;
  },

  deleteLead: async (leadId: string, entityId: string): Promise<void> => {
    await api.delete(`/crm/leads/${leadId}/by-entity/${entityId}`);
  },
};
